import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DashboardColorScheme, DashboardEmbedToken, EmbeddedDashboardHandle } from 'app/shared/models/dashboard-embed.model';
import { getDashboardEmbedErrorMessage, getRawDashboardEmbedErrorMessage } from 'app/shared/services/dashboard-embed/dashboard-embed-error.helper';
import { DashboardEmbedService } from 'app/shared/services/dashboard-embed/dashboard-embed.service';
import { Subject, Subscription, firstValueFrom, takeUntil } from 'rxjs';
import { DatabricksDashboardFactory } from './databricks-dashboard.factory';

/**
 * Renders the published Databricks AI/BI dashboard the backend issues a token for.
 *
 * The ticket asks for an iframe and there is one, but this component never writes it:
 * `@databricks/aibi-client` builds and owns it inside the container below. All this does is mint a
 * viewer token for the selected tenant, hand it over, and show what happened while that is in
 * progress — so a screen that wants the dashboard renders this and nothing else.
 *
 * **It takes no dashboard id.** Which dashboard a tenant gets is decided server-side and arrives on
 * the token response, so there is nothing here for a host to choose, to configure, or to get wrong.
 *
 * @example
 * ```html
 * <gains-embedded-dashboard label="Value Metric"></gains-embedded-dashboard>
 * ```
 */
@Component({
  selector: 'gains-embedded-dashboard',
  standalone: false,
  templateUrl: './gains-embedded-dashboard.component.html',
  styleUrl: './gains-embedded-dashboard.component.scss'
})
export class GainsEmbeddedDashboardComponent implements OnInit, OnDestroy {
  private static readonly LOAD_FAILED_MESSAGE = 'This dashboard could not be loaded right now. Please try again.';

  /** Names the embedded frame for assistive technology — the library gives it no title of its own. */
  @Input() label = 'Dashboard';

  @Input() colorScheme: DashboardColorScheme = 'light';

  /**
   * Static because the container must exist before the first token arrives, and it is never
   * `*ngIf`ed away for exactly that reason — see the note on the overlay in this component's SCSS.
   */
  @ViewChild('canvas', { static: true }) private readonly _canvas!: ElementRef<HTMLDivElement>;

  protected isLoading = false;

  protected loadError = '';

  private readonly destroy$ = new Subject<void>();

  private _dashboard: EmbeddedDashboardHandle | null = null;

  /**
   * The token request in flight, so a second load cancels it rather than racing it. Two overlapping
   * loads would both reach {@link renderDashboard}: each appends an iframe to the same container and
   * the first handle is overwritten, leaking its refresh timer and `window` message listener.
   */
  private _loadSubscription: Subscription | null = null;

  constructor(
    private readonly _dashboardEmbedService: DashboardEmbedService,
    private readonly _dashboardFactory: DatabricksDashboardFactory
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.cancelLoad();
    this.teardownDashboard();
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected get hasFailed(): boolean {
    return !!this.loadError;
  }

  protected retry(): void {
    this.load();
  }

  private load(): void {
    this.cancelLoad();
    this.teardownDashboard();
    this.loadError = '';
    this.isLoading = true;

    this._loadSubscription = this._dashboardEmbedService
      .getEmbedToken()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: token => this.renderDashboard(token),
        error: error => this.showLoadFailure(error)
      });
  }

  /**
   * Everything the embed needs comes off the response: which dashboard, and the workspace it lives
   * in. Neither is configured in the UI, and both differ per environment.
   */
  private renderDashboard(token: DashboardEmbedToken): void {
    this.isLoading = false;

    if (!token.dashboardId) {
      this.showLoadFailure(new Error('The embed token named no dashboard.'));
      return;
    }

    // The library validates its own options and can throw before anything is on screen. Uncaught,
    // that would leave neither an embed nor a failure state — a blank panel with no way forward.
    try {
      this._dashboard = this._dashboardFactory.create({
        container: this._canvas.nativeElement,
        instanceUrl: token.instanceUrl,
        workspaceId: token.workspaceId,
        dashboardId: token.dashboardId,
        token: token.token,
        colorScheme: this.colorScheme,
        getNewToken: () => this.mintReplacementToken()
      });

      this._dashboard.initialize();
      this.nameEmbeddedFrame();
    } catch (error) {
      this._dashboard = null;
      this.showLoadFailure(error);
    }
  }

  /**
   * The token lives 60 minutes and the library owns the renewal: it reads `exp`, schedules the
   * refresh and posts the new token into the iframe. So this answers on demand instead of the
   * component running a timer, which is what a viewer sitting on the screen for over an hour needs.
   */
  private mintReplacementToken(): Promise<string> {
    return firstValueFrom(this._dashboardEmbedService.getEmbedToken())
      .then(token => token.token)
      .catch(error => {
        // The library answers a rejection by replacing the iframe with an error page of its own,
        // which this component would otherwise know nothing about — no message, no Try again, and
        // an hour of work behind a dead panel. Raising it here puts our own retry over the top.
        this.showLoadFailure(error);
        throw error;
      });
  }

  /**
   * The library creates the iframe untitled, so a screen reader would announce an unnamed frame.
   *
   * It appends synchronously inside `initialize()` today; a version that defers that would leave the
   * frame unnamed silently, so say so rather than failing quietly.
   */
  private nameEmbeddedFrame(): void {
    const frame = this._canvas.nativeElement.querySelector('iframe');

    if (!frame) {
      console.warn('[GainsEmbeddedDashboard] No iframe to name — the embed is unlabelled for assistive technology.');
      return;
    }

    frame.setAttribute('title', this.label);
  }

  private showLoadFailure(error: unknown): void {
    this.isLoading = false;
    this.loadError = getDashboardEmbedErrorMessage(error, GainsEmbeddedDashboardComponent.LOAD_FAILED_MESSAGE);

    // The support reference code is stripped from what the user reads and is exactly what the backend
    // team needs to find the failure, so the untouched message goes to the console.
    console.error('[GainsEmbeddedDashboard] Embed token request failed:', getRawDashboardEmbedErrorMessage(error) ?? error);
  }

  private teardownDashboard(): void {
    this._dashboard?.destroy();
    this._dashboard = null;
  }

  private cancelLoad(): void {
    this._loadSubscription?.unsubscribe();
    this._loadSubscription = null;
  }
}
